import React from 'react'

import AvailableSlotsIndicator from '@/components/integration/nylas/scheduler/components/availability/AvailableSlotsIndicator'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import {
  getCalendarAnchorDate,
  getLocalDayKey,
  getSlotCountForDate,
} from '@/components/integration/nylas/scheduler/helpers'
import useSchedulerDays from '@/components/integration/nylas/scheduler/hooks/useSchedulerDays'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'

type CalendarComponents = NonNullable<
  React.ComponentProps<typeof Calendar>['components']
>

const CalendarDayButton: NonNullable<CalendarComponents['DayButton']> =
  function CalendarDayButton({
    children,
    modifiers: _modifiers,
    day,
    className: _className,
    ...props
  }) {
    const { timezone, owningEntityType, owningEntityId, availabilitySource } =
      useSchedulerConfig()
    const { selectedDate, selectedDateRange, availabilityFilter } =
      useSchedulerSelection()
    const { schedulerDays, isLoading } = useSchedulerDays(
      owningEntityType,
      owningEntityId,
      selectedDateRange.start,
      selectedDateRange.end,
      timezone,
      availabilityFilter,
      { availabilitySource },
    )

    const isSelected =
      selectedDate?.toISOString() ===
      getCalendarAnchorDate(day.date).toISOString()
    const availableSlots = getSlotCountForDate(
      getLocalDayKey(day.date),
      schedulerDays,
      timezone,
      isLoading,
    )
    const isAvailable = availableSlots > 0
    const isBeforeToday = day.date < new Date(new Date().setHours(0, 0, 0, 0))

    return (
      <Button
        type="button"
        variant={isSelected ? 'outline-primary' : 'ghost'}
        {...props}
        className={cn(
          `text-md flex h-full w-full flex-col items-center justify-start gap-0
          rounded-xl border p-0 py-2 text-center @lg:py-4 @lg:text-lg`,
          isSelected && 'hover:text-primary bg-blue-100 hover:bg-blue-100',
          isAvailable && !isBeforeToday
            ? 'text-dark cursor-pointer'
            : `cursor-not-allowed border-transparent opacity-50
              hover:bg-transparent`,
        )}
        onClick={(e) => {
          if (!isAvailable || isBeforeToday) {
            return
          }

          props.onClick?.(e)
        }}
      >
        <div className="flex flex-col items-center justify-start">
          <div className="font-bold">{children}</div>

          {!isBeforeToday && (
            <AvailableSlotsIndicator
              count={availableSlots}
              size="sm"
              isLoading={isLoading}
            />
          )}
        </div>
      </Button>
    )
  }

export default CalendarDayButton
